import React, { useState, useEffect, useCallback } from 'react';
import { paymentRecordsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import InvoicePick, { invoicePickPayload, invoicePickReady } from './InvoicePick';

// Document submission tracker (Q5): a per-student payment ledger — multiple
// entries can share a description (partial payments); total paid for an
// item is the sum of its entries.
// Founder rule, 19 Oct 2026: only the CFO records a payment. The CFO adds
// entries as Received and accepts/rejects the rest; other staff log an entry
// that waits for the CFO's check (the server enforces the same split).
const panelStyle = {
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  marginTop: 12,
};
const headingStyle = { color: '#9aa4c4', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 };
const inputStyle = {
  padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)', color: '#eee', fontSize: 12, boxSizing: 'border-box',
};

const DESCRIPTIONS = [
  ['APP_FEE', 'VisionExploit application fee'],
  ['INSTALLMENT_1', 'Installment 1'],
  ['INSTALLMENT_2_FINAL', 'Installment 2 (Final)'],
  ['SCHOOL_DEPOSIT', 'School offer-letter deposit'],
  ['SCHOOL_FINAL', 'School final payment'],
  ['ACCOMMODATION_FEE', 'Accommodation fee'],
  ['LANGUAGE_FEE', 'Language fee'],
  ['OTHER', 'Other'],
];
const DESCRIPTION_LABEL = Object.fromEntries(DESCRIPTIONS);
// VisionExploit's own fees: marking one Received issues its billing receipt
// against an invoice (founder, 24 Oct 2026). The rest is money paid to others.
const INVOICED_ITEMS = ['APP_FEE', 'INSTALLMENT_1', 'INSTALLMENT_2_FINAL'];
const errText = (err, fallback) => {
  const d = err?.response?.data;
  if (!d) return fallback;
  if (Array.isArray(d)) return d[0];
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};

const STATUS_STYLES = {
  UPLOADED_AWAITING_CHECK: { bg: 'rgba(245,166,35,0.15)', color: '#f5a623', label: 'Waiting for CFO / Chairman check' },
  RECEIVED: { bg: 'rgba(74,222,128,0.15)', color: '#4ade80', label: 'Received' },
  REJECTED: { bg: 'rgba(245,87,108,0.15)', color: '#f5576c', label: 'Rejected' },
};

const PaymentsPanel = ({ studentId, onChanged }) => {
  const { canRecordPayments } = useAuth();
  const isRecorder = canRecordPayments();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ description: 'INSTALLMENT_1', other_description: '', amount: '', currency: 'USD', receipt: null });
  const [saving, setSaving] = useState(false);
  const [pick, setPick] = useState({ invoice: '', reason: '' });
  const [acceptPick, setAcceptPick] = useState({});
  const [acceptingId, setAcceptingId] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await paymentRecordsAPI.list(studentId);
      setRecords(res.data.results || res.data || []);
    } catch (err) {
      setError('Could not load payment records.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (record) => {
    const needsInvoice = INVOICED_ITEMS.includes(record.description);
    if (needsInvoice && acceptingId !== record.id) { setAcceptingId(record.id); return; }
    const p = acceptPick[record.id] || {};
    if (needsInvoice && !invoicePickReady(p)) return;
    setBusyId(record.id);
    setError('');
    try {
      await paymentRecordsAPI.accept(record.id, needsInvoice ? invoicePickPayload(p) : {});
      setAcceptingId(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(errText(err, 'Could not accept this payment.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (record) => {
    if (!rejectNote.trim()) return;
    setBusyId(record.id);
    setError('');
    try {
      await paymentRecordsAPI.reject(record.id, rejectNote.trim());
      setRejectingId(null);
      setRejectNote('');
      await load();
      onChanged?.();
    } catch (err) {
      setError('Could not reject this payment.');
    } finally {
      setBusyId(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.amount) return;
    setSaving(true);
    setError('');
    try {
      const invoiced = isRecorder && INVOICED_ITEMS.includes(form.description);
      await paymentRecordsAPI.create({ student: studentId, ...form, ...(invoiced ? invoicePickPayload(pick) : {}) });
      setForm({ description: 'INSTALLMENT_1', other_description: '', amount: '', currency: 'USD', receipt: null });
      setPick({ invoice: '', reason: '' });
      setShowAddForm(false);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.other_description?.[0] || errText(err, 'Could not add this payment.'));
    } finally {
      setSaving(false);
    }
  };

  const totals = records.reduce((acc, r) => {
    if (r.status === 'REJECTED') return acc;
    const key = r.description === 'OTHER' ? (r.other_description || 'Other') : DESCRIPTION_LABEL[r.description];
    acc[key] = (acc[key] || 0) + parseFloat(r.amount || 0);
    return acc;
  }, {});

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={headingStyle}>PAYMENTS</div>
        <button type="button" onClick={() => setShowAddForm((v) => !v)}
          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#9aa4c4', fontSize: 11, cursor: 'pointer' }}>
          {showAddForm ? 'Cancel' : (isRecorder ? '+ Record payment' : '+ Log payment for CFO / Chairman')}
        </button>
      </div>
      {error && <div style={{ color: '#f5576c', fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {showAddForm && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10, padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
          <select style={{ ...inputStyle, minWidth: 180 }} value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}>
            {DESCRIPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          {form.description === 'OTHER' && (
            <input style={{ ...inputStyle, minWidth: 140 }} placeholder="Describe the item" value={form.other_description}
              onChange={(e) => setForm((f) => ({ ...f, other_description: e.target.value }))} />
          )}
          <input style={{ ...inputStyle, width: 100 }} type="number" step="0.01" min="0" placeholder="Amount" value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required />
          <input style={{ ...inputStyle, width: 70 }} value={form.currency}
            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} />
          <input type="file" style={{ fontSize: 11, color: '#9aa4c4' }}
            onChange={(e) => setForm((f) => ({ ...f, receipt: e.target.files?.[0] || null }))} />
          {isRecorder && INVOICED_ITEMS.includes(form.description) && (
            <div style={{ width: '100%', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ color: '#8a94b8', fontSize: 11 }}>Billing receipt against:</span>
              <InvoicePick studentId={studentId} currency={form.currency} value={pick} onChange={setPick}
                inputStyle={{ ...inputStyle, minWidth: 220 }} />
            </div>
          )}
          <button type="submit" disabled={saving || (isRecorder && INVOICED_ITEMS.includes(form.description) && !invoicePickReady(pick))}
            style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: '#4ade80', color: '#023', fontSize: 12, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
            {saving ? 'Saving…' : (isRecorder ? 'Record' : 'Send for check')}
          </button>
          {!isRecorder && (
            <div style={{ width: '100%', color: '#8a94b8', fontSize: 11 }}>
              Only the CFO or Chairman records payments. This waits for their check before it counts as received.
            </div>
          )}
        </form>
      )}

      {loading ? (
        <p style={{ color: '#9aa4c4', fontSize: 12 }}>Loading…</p>
      ) : records.length === 0 ? (
        <p style={{ color: '#8a94b8', fontSize: 12 }}>No payments recorded yet.</p>
      ) : (
        <>
          {Object.entries(totals).map(([label, total]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#8a94b8', marginBottom: 2 }}>
              <span>{label}</span><span style={{ color: '#ddd', fontWeight: 600 }}>{total.toFixed(2)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '8px 0' }} />
          {records.map((r) => {
            const st = STATUS_STYLES[r.status] || STATUS_STYLES.RECEIVED;
            return (
              <div key={r.id} style={{ padding: '8px 10px', marginBottom: 4, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: '#ddd', fontSize: 13 }}>
                    {r.description === 'OTHER' ? r.other_description : r.description_display} — {r.currency} {r.amount}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
                </div>
                {r.receipt_url && (
                  <a href={r.receipt_url} target="_blank" rel="noreferrer" style={{ color: '#4facfe', fontSize: 12 }}>View receipt</a>
                )}
                {r.billing_receipt_number && (
                  <div style={{ marginTop: 2, color: '#8a94b8', fontSize: 11 }}>Billing receipt {r.billing_receipt_number}</div>
                )}
                {r.status === 'RECEIVED' && !r.billing_receipt && INVOICED_ITEMS.includes(r.description) && (
                  <div style={{ marginTop: 2, color: '#f5a623', fontSize: 11 }}>No billing receipt — recorded before receipts were linked.</div>
                )}
                {acceptingId === r.id && r.status === 'UPLOADED_AWAITING_CHECK' && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: '#8a94b8', fontSize: 11 }}>Billing receipt against:</span>
                    <InvoicePick studentId={studentId} currency={r.currency} value={acceptPick[r.id] || { invoice: '', reason: '' }}
                      onChange={(v) => setAcceptPick((s) => ({ ...s, [r.id]: v }))} inputStyle={{ ...inputStyle, minWidth: 220 }} />
                  </div>
                )}
                {r.status === 'REJECTED' && r.rejection_note && (
                  <div style={{ marginTop: 4, color: '#f5a623', fontSize: 11 }}>Note: {r.rejection_note}</div>
                )}
                {r.status === 'UPLOADED_AWAITING_CHECK' && !isRecorder && (
                  <div style={{ marginTop: 4, color: '#8a94b8', fontSize: 11 }}>
                    {r.added_via_public_link ? 'Added by the student.' : `Logged by ${r.added_by_staff_name || 'staff'}.`} The CFO or Chairman accepts or rejects it.
                  </div>
                )}
                {r.status === 'UPLOADED_AWAITING_CHECK' && isRecorder && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" disabled={busyId === r.id || (acceptingId === r.id && !invoicePickReady(acceptPick[r.id] || {}))}
                      onClick={() => handleAccept(r)}
                      style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#4ade80', color: '#023', fontSize: 12, cursor: busyId === r.id ? 'wait' : 'pointer', fontWeight: 600 }}>
                      {acceptingId === r.id ? 'Confirm & issue receipt' : 'Accept'}
                    </button>
                    {rejectingId === r.id ? (
                      <>
                        <input type="text" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                          placeholder="Reason shown to the student"
                          style={{ ...inputStyle, minWidth: 160 }} />
                        <button type="button" disabled={busyId === r.id || !rejectNote.trim()} onClick={() => handleReject(r)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#f5576c', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                          Confirm reject
                        </button>
                        <button type="button" onClick={() => { setRejectingId(null); setRejectNote(''); }}
                          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#aaa', fontSize: 12, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button type="button" disabled={busyId === r.id} onClick={() => setRejectingId(r.id)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #f5576c55', background: 'transparent', color: '#f5576c', fontSize: 12, cursor: 'pointer' }}>
                        Reject
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default PaymentsPanel;
